import { Button, Form, Popconfirm, Segmented, Space, Table } from 'antd'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CopyOutlined, DeleteOutlined, EditOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons'
import { NamePath } from 'antd/lib/form/interface'
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { DndContext, DragEndEvent } from '@dnd-kit/core'
import DraggableTableRow from '../../../../../components/Table/DraggableTableRow'
import SectionFormVariableModal from './SectionFormVariableModal'
import { ChartYAxis } from '../chart/types'
import { CustomReportVariable, RowType, VariableParams } from '../../../../../redux/context/customReports/typesVariable'
import { getTitleByLanguage } from '../customReportTable/utils'
import CodeBlock from '../../../../../components/Misc/CodeBlock'
import DimensionName from '../../../../../features/dimension/components/DimensionName'
import CtxScenarioName from '../../../../../features/scenario/components/CtxScenarioName'

interface VariableTableProps {
  open: boolean
  variableNamePath?: NamePath
  hanldeVariableDelete: (val: CustomReportVariable) => void
  setVariableNamePath: (value?: NamePath) => void
  setOpen: (open: boolean) => void
}

type CustomReportVariableFormItem = CustomReportVariable & {
  key: number
}

const YAxisSegmented = React.memo<{ index: number }>(({ index }) => (
  <Form.Item initialValue={ChartYAxis.left} noStyle key={index} name={[index, 'style', 'yAxis']}>
    <Segmented
      size="small"
      options={[
        {
          label: <LeftOutlined />,
          value: ChartYAxis.left
        },
        {
          label: <RightOutlined />,
          value: ChartYAxis.right
        }
      ]}
    />
  </Form.Item>
))

const ActionButtons = React.memo<{
  variable: CustomReportVariable
  index: number
  onCopy: (variable: CustomReportVariable) => void
  onEdit: (path: NamePath) => void
  onDelete: (variable: CustomReportVariable, index: number) => void
  deleteText: string
  yesText: string
  cancelText: string
}>(({ variable, index, onCopy, onEdit, onDelete, deleteText, yesText, cancelText }) => (
  <Space>
    <Button type="text" size="small" onClick={() => onCopy(variable)} icon={<CopyOutlined />} />
    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => onEdit(['variables', index])} />
    <Popconfirm
      placement="leftBottom"
      title={deleteText}
      onConfirm={() => onDelete(variable, index)}
      okText={yesText}
      cancelText={cancelText}
    >
      <Button size="small" danger type="text" icon={<DeleteOutlined />} />
    </Popconfirm>
  </Space>
))

const VariableTable: React.FC<VariableTableProps> = ({
  open,
  variableNamePath,
  setOpen,
  hanldeVariableDelete,
  setVariableNamePath
}) => {
  const { t } = useTranslation()
  const form = Form.useFormInstance()
  const variables = (Form.useWatch('variables', form) || []) as CustomReportVariableFormItem[]

  const translations = useMemo(
    () => ({
      name: t('global:name'),
      dataType: t('global:dataType'),
      scenario: t('global:scenario'),
      dimension: t('global:dimension'),
      formula: t('global:formula'),
      yAxis: t('global:yAxis'),
      actions: t('global:actions'),
      emptyRow: t('table:empty-row'),
      deleteConfirm: t('global:delete-confirm'),
      yes: t('global:yes'),
      cancel: t('global:cancel'),
      addRow: t('customReportPage:add-row')
    }),
    [t]
  )

  const dataSource = useMemo(
    () =>
      variables.map(v => ({
        ...v,
        children: undefined
      })),
    [variables]
  )

  const sortableItems = useMemo(() => variables.map(i => i.key), [variables])

  const copyVariable = useCallback(
    (data: CustomReportVariable) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, name, ...rest } = data
      const vars = variables || []
      const nextId = Math.max(...vars.map(({ key }: { key: number }) => +key)) + 1

      const variableCopy = {
        ...rest,
        key: nextId.toString(),
        name: name ? `${name} (copy)` : undefined
      }

      form.setFieldsValue({
        variables: [...vars, variableCopy]
      })
    },
    [variables, form]
  )

  const showVariableModal = useCallback(
    (path?: NamePath) => {
      setVariableNamePath(path)
      setOpen(true)
    },
    [setVariableNamePath, setOpen]
  )

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (active.id !== over?.id) {
        const activeIndex = variables.findIndex(i => i.key === active.id)
        const overIndex = variables.findIndex(i => i.key === over?.id)
        const newVariables = arrayMove(variables, activeIndex, overIndex)
        form.setFieldValue('variables', newVariables)
      }
    },
    [variables, form]
  )

  const handleDelete = useCallback(
    (variable: CustomReportVariable, index: number, remove: (index: number) => void) => {
      hanldeVariableDelete(variable)
      remove(index)
    },
    [hanldeVariableDelete]
  )

  const renderName = useCallback(
    (title: string, row: CustomReportVariable) =>
      row.rowType === RowType.empty ? translations.emptyRow : getTitleByLanguage(row),
    [translations.emptyRow]
  )

  const renderDataType = useCallback(
    ([dataType]: [VariableParams['dataType'], number | undefined] = [undefined, undefined]) =>
      t(`global:${dataType}`, ''),
    [t]
  )

  const renderScenario = useCallback(
    ([, id]: [VariableParams['dataType'], number | undefined] = [undefined, undefined]) => (
      <CtxScenarioName scenarioId={id} />
    ),
    []
  )

  const renderDimension = useCallback((id: string) => <DimensionName dimensionId={id} />, [])

  const renderFormula = useCallback((source: string) => <CodeBlock code={source} />, [])

  const renderYAxis = useCallback(
    (val: any, variable: CustomReportVariable, index: number) => <YAxisSegmented index={index} />,
    []
  )

  const columns = useMemo(
    () => [
      {
        key: 'key',
        width: 30
      },
      {
        title: translations.name,
        dataIndex: 'name',
        ellipsis: true,
        render: renderName
      },
      {
        title: translations.dataType,
        dataIndex: ['params', 'dataType'],
        ellipsis: true,
        render: renderDataType
      },
      {
        title: translations.scenario,
        dataIndex: ['params', 'dataType'],
        ellipsis: true,
        render: renderScenario
      },
      {
        title: translations.dimension,
        dataIndex: ['params', 'dimension', 'dimensionId'],
        render: renderDimension
      },
      {
        title: 'Offset',
        dataIndex: ['params', 'offset'],
        width: 80
      },
      {
        title: translations.formula,
        dataIndex: 'source',
        ellipsis: true,
        render: renderFormula
      },
      {
        title: translations.yAxis,
        dataIndex: 'yAxis',
        width: 60,
        render: renderYAxis
      }
    ],
    [translations, renderName, renderDataType, renderScenario, renderDimension, renderFormula, renderYAxis]
  )

  return (
    <>
      <SectionFormVariableModal variableNamePath={variableNamePath} open={open} onCancel={() => setOpen(false)} />
      <Form.List name="variables">
        {(fields, { remove }) => (
          <DndContext onDragEnd={onDragEnd}>
            <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
              <Table
                data-cy="variable-table"
                size="small"
                dataSource={dataSource}
                columns={[
                  ...columns,
                  {
                    key: 'actions',
                    title: translations.actions,
                    width: 80,
                    render: (val: any, variable: CustomReportVariable, index: number) => (
                      <ActionButtons
                        variable={variable}
                        index={index}
                        onCopy={copyVariable}
                        onEdit={showVariableModal}
                        onDelete={(v, i) => handleDelete(v, i, remove)}
                        deleteText={translations.deleteConfirm}
                        yesText={translations.yes}
                        cancelText={translations.cancel}
                      />
                    )
                  }
                ]}
                pagination={false}
                components={{
                  body: {
                    row: DraggableTableRow
                  }
                }}
                rowKey="key"
              />
            </SortableContext>
          </DndContext>
        )}
      </Form.List>
      <Button data-cy="add-variable" type="dashed" block htmlType="button" onClick={() => showVariableModal()}>
        {translations.addRow}
      </Button>
    </>
  )
}

export default React.memo(VariableTable)
